import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TermsCheckbox } from "@/components/ui/TermsCheckbox";
import { UsernameInput } from "@/components/ui/UsernameInput";
import { useToast } from "@/hooks/use-toast";

export function CompleteProfile() {
  const { completeProfileSetup } = useAuth();
  const [username, setUsername] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError("Username cannot be empty.");
      return;
    }
    
    if (!isUsernameValid) {
      toast({
        title: "Username Validation Required",
        description: "Please validate your username before continuing.",
        variant: "destructive"
      });
      return;
    }
    
    if (!acceptedTerms) {
      toast({
        title: "Terms and Conditions Required",
        description: "Please accept the Terms and Conditions to continue.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await completeProfileSetup(username);
      
      // Add a small delay to allow Firebase to complete its operations
      // and avoid AbortError when navigating away
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Use window.location to force a full page reload and bypass wouter routing issues
      // This will trigger the app's authentication flow to redirect to the appropriate dashboard
      window.location.href = "/";
    } catch (err: any) {
      console.error("Error completing profile setup:", err);
      
      // Provide user-friendly error messages
      let errorMessage = "Failed to update profile. Please try again.";
      
      if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
        errorMessage = "You don't have permission to update your profile. Please contact support.";
      } else if (err?.code === 'unavailable' || err?.message?.includes('unavailable')) {
        errorMessage = "Service temporarily unavailable. Please check your connection and try again.";
      } else if (err?.message?.includes('user')) {
        errorMessage = "User session expired. Please sign in again.";
      }
      
      setError(errorMessage);
      toast({
        title: "Profile Update Failed",
        description: errorMessage,
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background font-manrope p-4 sm:p-6 lg:p-8">
      <Card className="w-full max-w-sm sm:max-w-md lg:max-w-lg shadow-lg">
        <CardHeader className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4">
          <CardTitle className="text-center text-xl sm:text-2xl lg:text-3xl font-grotesk leading-tight">
            Complete Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-6 sm:pb-8">
          <p className="mb-4 sm:mb-6 text-center text-sm sm:text-base text-muted-foreground leading-relaxed">
            Please choose a username you are happy to be seen on the TAP leaderboards.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <UsernameInput
              value={username}
              onChange={setUsername}
              disabled={loading}
              required
              label="Username"
              placeholder="Enter your username"
              onValidationChange={setIsUsernameValid}
            />
            <TermsCheckbox
              checked={acceptedTerms}
              onCheckedChange={setAcceptedTerms}
              disabled={loading}
              className="pt-1 sm:pt-2"
            />
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md border border-destructive/20">
                {error}
              </p>
            )}
            <Button 
              type="submit" 
              variant="primary" 
              size="primary" 
              className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium" 
              disabled={loading || !acceptedTerms || !isUsernameValid}
            >
              {loading ? "Saving..." : "Save and Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
